import { Component, OnInit, OnDestroy, Renderer2, AfterViewInit } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { HtmlTemplatesService } from '../../services/html-templates.service';
import { UrlConstants } from '../../../shared/utils/url-constants';
import { AppConfigService } from '../../services/app-config.service';
import packageInfo from '../../../../../package.json';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styles: [`
    /* Enhanced styling for conversation trigger */
    ::ng-deep .conversation-trigger.with-text button {
      background-color: #2543b9 !important;
    }
    
    ::ng-deep .conversation-trigger.with-text .conversation-trigger-text {
      color: #ffffff !important;
    }

    /* Hide French text during loading and replace with English */
    ::ng-deep .conversation-trigger-text:not([data-english-set]) {
      font-size: 0 !important;
    }

    ::ng-deep .conversation-trigger-text:not([data-english-set])::before {
      content: "How can I help you?" !important;
      font-size: 14px !important;
      color: #ffffff !important;
    }

    ::ng-deep .conversation-window .mat-mdc-icon-button {
      color: white !important;
    }

    /* Conversation toolbar styling */
    ::ng-deep .conversation-toolbar.mat-toolbar {
      background-color: #2543b9 !important;
    }

    /* Position chatbox - Bottom Right (Default) */
    ::ng-deep .conversation-trigger {
      position: fixed !important;
      bottom: 40px !important;
      right: 20px !important;
      z-index: 9999 !important;
    }

    /* Optional: Adjust chatbox window position */
    ::ng-deep .conversation-window {
      position: fixed !important;
      bottom: 90px !important;
      right: 20px !important;
      z-index: 9998 !important;
    }
  `]
})
export class FooterComponent implements OnInit, OnDestroy, AfterViewInit {
  UrlConstants = UrlConstants;
  AppConfigService = AppConfigService;
  appVersion = packageInfo.version;
  private static chatboxInitialized = false;
  private static scriptLoaded = false;
  private languageOverrideInterval: any;
  private triggerTextLoadingInterval: any; // New interval specifically for loading period
  private hoverTimeout: any;
  private chatboxHoverObserver: MutationObserver | null = null;
  
  constructor(
    public authService: AuthService,
    private renderer: Renderer2,
    public htmlTemplatesService: HtmlTemplatesService
  ) {}

  ngOnInit(): void {
    if (this.authService.isLoggedIn()) {
      this.initializeChatbox();
      this.startContinuousLanguageMonitoring();
      this.startTriggerTextLoadingMonitoring(); // New method
    }
  }

  ngAfterViewInit(): void {
    // Immediate override for any existing elements
    setTimeout(() => {
      this.forceTriggerTextEnglish();
      this.attachHoverListenersToTrigger();
      this.observeForNewTriggers();
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.languageOverrideInterval) {
      clearInterval(this.languageOverrideInterval);
    }
    if (this.triggerTextLoadingInterval) {
      clearInterval(this.triggerTextLoadingInterval);
    }
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
    if (this.chatboxHoverObserver) {
      this.chatboxHoverObserver.disconnect();
    }
  }

  private attachHoverListenersToTrigger(): void {
    const triggerElement = document.querySelector('.conversation-trigger');
    
    if (triggerElement && !triggerElement.hasAttribute('data-hover-attached')) {
      triggerElement.setAttribute('data-hover-attached', 'true');
      
      triggerElement.addEventListener('mouseenter', () => {
        this.hideTextOnHover();
      });
    }
  }

  /**
   * NEW: Hide text on hover and show after 5 seconds
   */
  private hideTextOnHover(): void {
    const textElements = document.querySelectorAll('.conversation-trigger-text');
    
    // Hide text immediately
    textElements.forEach((element: Element) => {
      const htmlElement = element as HTMLElement;
      htmlElement.style.display = 'none';
      htmlElement.style.visibility = 'hidden';
      htmlElement.setAttribute('data-text-hidden', 'true');
    });
    
    // Clear any existing timeout
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
    
    // Show text after 5 seconds (5000ms)
    this.hoverTimeout = setTimeout(() => {
      textElements.forEach((element: Element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.display = '';
        htmlElement.style.visibility = '';
        htmlElement.removeAttribute('data-text-hidden');
      });
    }, 5000);
  }

  private observeForNewTriggers(): void {
    const config = { 
      childList: true, 
      subtree: true, 
      attributes: false, 
      characterData: false 
    };
    
    this.chatboxHoverObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          const newTrigger = document.querySelector(
            '.conversation-trigger:not([data-hover-attached])'
          );
          if (newTrigger) {
            setTimeout(() => {
              this.attachHoverListenersToTrigger();
            }, 100);
          }
        }
      });
    });
    
    if (document.body) {
      this.chatboxHoverObserver.observe(document.body, config);
    }
  }

  // NEW METHOD: Intensive monitoring during loading period
  private startTriggerTextLoadingMonitoring(): void {
    let checkCount = 0;
    const maxChecks = 50; // Check for 5 seconds at 100ms intervals
    
    this.triggerTextLoadingInterval = setInterval(() => {
      this.forceTriggerTextEnglish();
      checkCount++;
      
      // Stop intensive checking after 5 seconds
      if (checkCount >= maxChecks) {
        clearInterval(this.triggerTextLoadingInterval);
      }
    }, 100); // Check every 100ms during loading
  }

  // NEW METHOD: Force trigger text to English immediately
  private forceTriggerTextEnglish(): void {
    // Method 1: Direct text content replacement
    const triggerElements = document.querySelectorAll('.conversation-trigger-text');
    triggerElements.forEach((element: HTMLElement) => {
      const currentText = element.textContent?.trim();
      
      // Check if it's French text or empty/loading
      if (!currentText || 
          currentText.includes('Comment puis-je') || 
          currentText.includes('Puis-je vous aider') ||
          currentText === '' ||
          currentText.length < 3) {
        
        element.textContent = 'How can I help you?';
        element.setAttribute('data-english-set', 'true');
        
        // Apply styling directly
        element.style.backgroundColor = '#2543b9';
        element.style.color = '#ffffff';
        element.style.padding = '8px 12px';
        element.style.borderRadius = '4px';
       }
    });

    // Method 2: Also check for generic trigger elements
    const genericTriggers = document.querySelectorAll('.conversation-trigger, [class*="trigger"]');
    genericTriggers.forEach((element: HTMLElement) => {
      const textElement = element.querySelector('.conversation-trigger-text, [class*="trigger-text"]');
      if (textElement) {
        const currentText = textElement.textContent?.trim();
        if (!currentText || 
            currentText.includes('Comment puis-je') || 
            currentText.includes('Puis-je vous aider') ||
            currentText === '' ||
            currentText.length < 3) {
          
          textElement.textContent = 'How can I help you?';
          (textElement as HTMLElement).setAttribute('data-english-set', 'true');
          
          // Apply styling
          (textElement as HTMLElement).style.backgroundColor = '#2543b9';
          (textElement as HTMLElement).style.color = '#ffffff';
          (textElement as HTMLElement).style.padding = '8px 12px';
          (textElement as HTMLElement).style.borderRadius = '4px';
        }
      }
    });
  }

  private initializeChatbox(): void {
    if (FooterComponent.chatboxInitialized) {
      return;
    }

    if (customElements.get('wlcp-chatbox')) {
      FooterComponent.chatboxInitialized = true;
      // Even if already initialized, force trigger text
      setTimeout(() => this.forceTriggerTextEnglish(), 100);
      return;
    }

    this.interceptAllChatboxAPIs();
    this.setDocumentLanguage();
    this.setChatboxConfig();
    this.loadChatboxStyles();
    this.loadChatboxScript();
  }

  private interceptAllChatboxAPIs(): void {
    if ((window as any).wlcpApiIntercepted) {
      return;
    }
    (window as any).wlcpApiIntercepted = true;

    const originalFetch = window.fetch;
    const self = this;
    
    window.fetch = function(input: RequestInfo | URL, init?: RequestInit) {
      return originalFetch.call(this, input, init).then(response => {
        const url = typeof input === 'string' ? input : input.toString();
        
        // Intercept webchat settings API
        if (url.includes('/api/bot/webchatSettings/')) {
          return response.clone().json().then(data => {
            const englishData = {
              ...data,
              triggerText: "How can I help you?",
              welcomeMessage: "Hello! How can I assist you today?",
              placeholderText: "Type your message here...",
              sendButtonText: "Send",
              minimizeText: "Minimize",
              closeText: "Close",
              restartButtonText: "Start New Conversation",
              thankYouMessage: "Thank you for using our support chat!"
            };
            
            // Force trigger text after API response
            setTimeout(() => self.forceTriggerTextEnglish(), 200);
            
            return new Response(JSON.stringify(englishData), {
              status: response.status,
              statusText: response.statusText,
              headers: response.headers
            });
          });
        }
        
        // Intercept conversation finish API
        if (url.includes('/api/conversations/') && url.includes('/finish')) {
          // After conversation finishes, reapply language settings
          setTimeout(() => {
            self.reapplyLanguageSettings();
            self.forceTriggerTextEnglish(); // Add this
          }, 500);
          
          setTimeout(() => {
            self.reapplyLanguageSettings();
            self.forceTriggerTextEnglish(); // Add this
          }, 1500);
          
          setTimeout(() => {
            self.reapplyLanguageSettings();
            self.forceTriggerTextEnglish(); // Add this
          }, 3000);
        }
        
        // Intercept any other chatbot-related APIs that might reset language
        if (url.includes('/api/conversations/') || url.includes('/api/bot/')) {
          setTimeout(() => {
            self.overrideChatboxText();
            self.forceTriggerTextEnglish(); // Add this
          }, 200);
        }
        
        return response;
      });
    };

    // Also intercept XMLHttpRequest
    const originalXHRSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function(body) {
      this.addEventListener('load', function() {
        if (this.responseURL.includes('/api/conversations/') && this.responseURL.includes('/finish')) {
          setTimeout(() => {
            self.reapplyLanguageSettings();
            self.forceTriggerTextEnglish(); // Add this
          }, 500);
        }
      });
      return originalXHRSend.call(this, body);
    };
  }

  private reapplyLanguageSettings(): void {
    // Force document language again
    this.setDocumentLanguage();
    
    // Reapply chatbox configuration
    this.forceChatboxLanguageSettings();
    
    // Override text content
    this.overrideChatboxText();
    
    // ENHANCED: Also force trigger text
    this.forceTriggerTextEnglish();

  }

  private startContinuousLanguageMonitoring(): void {
    // Monitor and override language every 2 seconds
    this.languageOverrideInterval = setInterval(() => {
      this.overrideChatboxText();
      this.checkAndFixLanguage();
      this.forceTriggerTextEnglish(); // Add this to existing monitoring
    }, 2000);
  }

  private loadChatboxScript(): void {
    if (FooterComponent.scriptLoaded || document.querySelector('script[src*="wlcp-chatbox.js"]')) {
      FooterComponent.chatboxInitialized = true;
      // Force trigger text even if script already loaded
      setTimeout(() => this.forceTriggerTextEnglish(), 500);
      return;
    }

    const script = this.renderer.createElement('script');
    script.src = 'https://qa.demo.dev.wlcp-product.gcp.meshcore.net/webcomponent/wlcp-chatbox.js';
    script.charset = 'utf-8';
    
    script.onload = () => {
      FooterComponent.scriptLoaded = true;
      FooterComponent.chatboxInitialized = true;
      
      setTimeout(() => {
        this.reapplyLanguageSettings();
        this.forceTriggerTextEnglish(); // ENHANCED: Add this
      }, 1000);
      
      // Additional check after script load
      setTimeout(() => this.forceTriggerTextEnglish(), 2000);
    };
    
    this.renderer.appendChild(document.body, script);
  }

  private overrideChatboxText(): void {
    // ENHANCED: Add trigger text to replacements
    this.replaceTextInElements([
      { french: "Comment puis-je vous aider ?", english: "How can I help you?" },
      { french: "Comment puis-je vous aider?", english: "How can I help you?" },
      { french: "Puis-je vous aider ?", english: "How can I help you?" },
      { french: "Puis-je vous aider?", english: "How can I help you?" },
      { french: "Tapez votre message ici", english: "Type your message here" },
      { french: "Envoyer", english: "Send" },
      { french: "Fermer", english: "Close" },
      { french: "Réduire", english: "Minimize" },
      { french: "Bonjour", english: "Hello" },
      { french: "Merci", english: "Thank you" },
      { french: "Au revoir", english: "Goodbye" },
      { french: "Nouvelle conversation", english: "New conversation" },
      { french: "Redémarrer", english: "Restart" }
    ]);
    
    // After text replacement, force trigger text again
    this.forceTriggerTextEnglish();
  }

  // Keep all your existing methods unchanged...
  private forceChatboxLanguageSettings(): void {
    try {
      // Re-set global configuration
      (window as any).WLCP_CONFIG = {
        ...(window as any).WLCP_CONFIG,
        language: 'en',
        locale: 'en-US',
        lang: 'en',
        defaultLanguage: 'en'
      };

      // Try to access chatbox element and force language
      const chatboxElement = document.querySelector('wlcp-chatbox') as any;
      if (chatboxElement) {
        chatboxElement.setAttribute('lang', 'en');
        chatboxElement.setAttribute('data-language', 'en');
        
        if (typeof chatboxElement.setLanguage === 'function') {
          chatboxElement.setLanguage('en');
        }
        
        if (chatboxElement.language !== undefined) {
          chatboxElement.language = 'en';
        }

        // Dispatch language change events
        chatboxElement.dispatchEvent(new CustomEvent('language-change', {
          detail: { language: 'en', locale: 'en-US' }
        }));
      }

      // Dispatch global language events
      document.dispatchEvent(new CustomEvent('wlcp-language-change', {
        detail: { language: 'en', locale: 'en-US' }
      }));

    } catch (error) {
      console.warn('Could not force chatbox language settings:', error);
    }
  }

  private checkAndFixLanguage(): void {
    const chatboxElement = document.querySelector('wlcp-chatbox');
    if (chatboxElement) {
      // Check if any French text is present
      const hasFrenchText = chatboxElement.textContent?.includes('Comment puis-je vous aider') ||
                           chatboxElement.textContent?.includes('Bonjour') ||
                           chatboxElement.innerHTML?.includes('Comment puis-je vous aider');
      
      if (hasFrenchText) {
         this.reapplyLanguageSettings();
      }
    }
  }

  private setDocumentLanguage(): void {
    document.documentElement.lang = 'en';
    document.documentElement.setAttribute('data-language', 'en');
    document.body.setAttribute('data-chatbox-lang', 'en');
  }

  private setChatboxConfig(): void {
    (window as any).WLCP_CONFIG = {
      bot: {
        url: 'https://qa.demo.dev.wlcp-product.gcp.meshcore.net',
        language: 'en',
        locale: 'en-US'
      },
      language: 'en',
      locale: 'en-US',
      lang: 'en',
      defaultLanguage: 'en',
      preferredLanguage: 'en',
      forceLanguage: 'en',
      i18n: {
        lng: 'en',
        fallbackLng: 'en',
        supportedLngs: ['en']
      }
    };

    (window as any).chatboxLanguage = 'en';
    (window as any).preferredLanguage = 'en';
  }

  private loadChatboxStyles(): void {
    const existingLink = document.querySelector('link[href*="wlcp-chatbox.css"]');
    if (existingLink) {
      return;
    }

    const link = this.renderer.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://qa.demo.dev.wlcp-product.gcp.meshcore.net/webcomponent/wlcp-chatbox.css';
    this.renderer.appendChild(document.head, link);
  }

  private replaceTextInElements(replacements: {french: string, english: string}[]): void {
    const chatboxElement = document.querySelector('wlcp-chatbox');
    if (!chatboxElement) return;

    const walker = document.createTreeWalker(
      chatboxElement,
      NodeFilter.SHOW_TEXT
    );

    const textNodes: Text[] = [];
    let node;
    while (node = walker.nextNode()) {
      textNodes.push(node as Text);
    }

    textNodes.forEach(textNode => {
      if (textNode.textContent) {
        replacements.forEach(replacement => {
          if (textNode.textContent!.includes(replacement.french)) {
            textNode.textContent = textNode.textContent!.replace(replacement.french, replacement.english);
          }
        });
      }
    });

    // Also check for elements with specific attributes or classes
    const elementsWithText = chatboxElement.querySelectorAll('[data-text], .message-text, .trigger-text, .placeholder-text');
    elementsWithText.forEach(element => {
      replacements.forEach(replacement => {
        if (element.textContent?.includes(replacement.french)) {
          element.textContent = element.textContent.replace(replacement.french, replacement.english);
        }
        
        if (element.getAttribute('data-text')?.includes(replacement.french)) {
          element.setAttribute('data-text', replacement.english);
        }
      });
    });
  }

  onNavigateToUserGuide(): void {
    if (this.authService.isLoggedIn()) {
      this.htmlTemplatesService.getUserGuideUrl().subscribe(url => {
        window.open(url, '_blank');
      });
    }
  }


  onNavigateToPolicy(): void {
    if (this.authService.isLoggedIn()) {
      this.htmlTemplatesService.getPolicyUrl().subscribe(url => {
        window.open(url, '_blank');
      });
    }
  }


  onNavigateToTermofUse(){
if (this.authService.isLoggedIn()) {
      this.htmlTemplatesService.getTermUrl().subscribe(url => {
        window.open(url, '_blank');
      });
    }
  }
  
  

}